/**
 * Normalizes the access errors the learner endpoints return so pages can render an in-place
 * 403/404 instead of a blank screen or an endless loader.
 *
 * The backend attaches a `context` block to every 403/404 a learner can reach by typing a URL
 * (see `_access_error` in server/main/apis/main_app_apis.py):
 *
 *   { error: { code, message, context: { reason, course_id, course_name, provider_name, registered } } }
 *
 * `reason` is the discriminator the error pages switch on; `code` is kept for older payloads.
 *
 * A reflection lesson whose role play is unfinished is refused with a 409 in the same shape. The
 * server calls it "locked", but the way out is different - play one role play, not finish the
 * items before this one - so it gets a reason of its own here.
 */

export const ACCESS_REASON = {
  NOT_REGISTERED: "not_registered",
  DRAFT: "draft",
  LOCKED: "locked",
  LANGUAGE: "not_available_in_language",
  ONBOARDING: "onboarding_required",
  REQUIRED_INFO: "required_info_missing",
  NOT_FOUND: "not_found",
  REFLECTION_SOURCE: "reflection_source_missing",
};

// The 409s that are access refusals. Any other 409 is a conflict its own caller handles.
const REFLECTION_CODES = ["REFLECTION_SOURCE_MISSING", "REFLECTION_SOURCE_UNAVAILABLE"];

const CODE_TO_REASON = {
  CONTENT_LOCKED: ACCESS_REASON.LOCKED,
  ONBOARDING_REQUIRED: ACCESS_REASON.ONBOARDING,
  REQUIRED_USER_INFO_MISSING: ACCESS_REASON.REQUIRED_INFO,
  NOT_AVAILABLE_IN_LANGUAGE: ACCESS_REASON.LANGUAGE,
  NOT_FOUND: ACCESS_REASON.NOT_FOUND,
};

/**
 * @returns {null | {status: number, code: string, reason: string, courseId: (string|number|null),
 *   courseName: string, providerName: string, registered: boolean}}
 *   `null` for anything that is not a 403/404 or a reflection-source 409 — those stay with their
 *   existing handling.
 */
export const parseAccessError = (error) => {
  const status = error?.response?.status;
  const payload = error?.response?.data?.error || error?.response?.data || {};
  const isReflectionRefusal = status === 409 && REFLECTION_CODES.includes(payload?.code);
  if (status !== 403 && status !== 404 && !isReflectionRefusal) return null;

  const context = payload?.context || {};
  const code = payload?.code || (status === 404 ? "NOT_FOUND" : "FORBIDDEN");
  const reason = isReflectionRefusal
    ? ACCESS_REASON.REFLECTION_SOURCE
    : context?.reason ||
      CODE_TO_REASON[code] ||
      (status === 404 ? ACCESS_REASON.NOT_FOUND : ACCESS_REASON.NOT_REGISTERED);

  return {
    status,
    code,
    reason,
    courseId: context?.course_id ?? null,
    courseName: context?.course_name || "",
    providerName: context?.provider_name || "",
    // Absent context (an older payload, or a 404) reads as "not registered": the page then offers
    // home rather than a back-link into a program we can't confirm the viewer belongs to.
    registered: Boolean(context?.registered),
  };
};

/** True when the error is a plain "this thing does not exist". */
export const isNotFoundError = (accessError) => accessError?.status === 404;
